import { ChevronDown, ChevronUp, LoaderCircle, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DateRangePicker } from './DateRangePicker';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from './ui/Accordion';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/Select';

import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { Label } from '@/components/ui/Label';
import { useAuth } from '@/contexts/AuthContext';
import {
  SearchFilter,
  SemanticSearchFilters,
  SemanticSearchFiltersResponse,
} from '@/services/ProductsService';

interface SearchProductsFilterProps {
  searchFilters: Partial<SearchFilter>;
  onSearchFilters: <K extends keyof SearchFilter>(
    filterKey: K,
    filterValue: SearchFilter[K] | undefined,
  ) => void;
  allowSemanticSearch?: boolean;
  useSemanticSearch?: boolean;
  semanticFilters?: SemanticSearchFilters;
  semanticFilterFacets?: SemanticSearchFiltersResponse['availableFilters'];
  isSemanticFiltersLoading?: boolean;
  onSemanticFiltersChange?: (filters: SemanticSearchFilters) => void;
}

function formatFacetLabel(value: string) {
  return value
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

export function SearchProductsFilter({
  searchFilters,
  onSearchFilters,
  allowSemanticSearch = false,
  useSemanticSearch = false,
  semanticFilters = { categories: [], features: {} },
  semanticFilterFacets,
  isSemanticFiltersLoading = false,
  onSemanticFiltersChange,
}: SearchProductsFilterProps) {
  const [showFilters, setShowFilters] = useState<boolean>(false);
  const [searchFiltersCount, setSearchFilterCount] = useState(0);

  const { t } = useTranslation('translation', {
    keyPrefix: 'components.SearchProductsFilter',
  });

  const { user } = useAuth();

  const visionCategoriesAvailable =
    typeof user?.customer?.visionCategoriesAvailable === 'string'
      ? user?.customer.visionCategoriesAvailable
          .split(',')
          .map((v) => v.trim())
          .sort()
      : [];

  const featureEntries = useMemo(
    () => Object.entries(semanticFilterFacets?.features ?? {}),
    [semanticFilterFacets?.features],
  );

  function handleClearFilters() {
    onSearchFilters('pairsWith', undefined);
    onSearchFilters('availableProduct', undefined);
    onSearchFilters('visionCategory', undefined);
    onSearchFilters('dateFrom', undefined);

    if (useSemanticSearch) {
      onSemanticFiltersChange?.({ categories: [], features: {} });
    }
  }

  function handleToggleSemanticCategory(category: string, checked: boolean) {
    const currentCategories = semanticFilters.categories ?? [];
    const nextCategories = checked
      ? [...new Set([...currentCategories, category])]
      : currentCategories.filter((value) => value !== category);

    onSemanticFiltersChange?.({
      ...semanticFilters,
      categories: nextCategories,
    });
  }

  function handleToggleSemanticFeature(
    featureKey: string,
    optionValue: string,
    checked: boolean,
  ) {
    const currentValues = semanticFilters.features?.[featureKey] ?? [];
    const nextValues = checked
      ? [...new Set([...currentValues, optionValue])]
      : currentValues.filter((value) => value !== optionValue);

    const nextFeatures = {
      ...(semanticFilters.features ?? {}),
    };

    if (nextValues.length > 0) {
      nextFeatures[featureKey] = nextValues;
    } else {
      delete nextFeatures[featureKey];
    }

    onSemanticFiltersChange?.({
      ...semanticFilters,
      features: nextFeatures,
    });
  }

  useEffect(() => {
    if (allowSemanticSearch && useSemanticSearch) {
      const semanticCount =
        (semanticFilters.categories?.length ?? 0) +
        Object.values(semanticFilters.features ?? {}).reduce(
          (acc, values) => acc + values.length,
          0,
        ) +
        (searchFilters.availableProduct ? 1 : 0);

      setSearchFilterCount(semanticCount);
      return;
    }

    const searchFiltersKeys: (keyof SearchFilter)[] = [
      'pairsWith',
      'availableProduct',
      'visionCategory',
      'dateFrom',
    ];

    const count = searchFiltersKeys.reduce((acc, key) => {
      return acc + (searchFilters[key] ? 1 : 0);
    }, 0);

    setSearchFilterCount(count);
  }, [
    allowSemanticSearch,
    searchFilters,
    semanticFilters.categories,
    semanticFilters.features,
    useSemanticSearch,
  ]);

  return (
    <div className="mb-6">
      <Button
        variant="outline"
        onClick={() => setShowFilters(!showFilters)}
        className="flex items-center gap-2"
      >
        {showFilters ? (
          <ChevronUp className="h-4 w-4" />
        ) : (
          <ChevronDown className="h-4 w-4" />
        )}
        {t('showButtonLabel')}
        {searchFiltersCount > 0 && (
          <Badge variant="secondary" className="ml-2">
            {searchFiltersCount}
          </Badge>
        )}
      </Button>

      <div className="mt-4 flex items-center gap-4">
        {searchFiltersCount > 0 && (
          <Badge variant="secondary">
            {t('activeFilterBadge', { count: searchFiltersCount })}
          </Badge>
        )}

        {searchFiltersCount > 0 && (
          <Button variant="outline" size="sm" onClick={handleClearFilters}>
            <X className="mr-1 h-4 w-4" />
            {t('clear')}
          </Button>
        )}
      </div>

      {showFilters && !useSemanticSearch && (
        <div className="mt-6 space-y-4">
          <div className="flex flex-col gap-4 md:flex-row">
            {visionCategoriesAvailable.length > 0 && (
              <div className="max-w-[200px] rounded-lg bg-white">
                <h3 className="mb-3 text-sm font-medium text-gray-700">
                  {t('category')}
                </h3>

                <Select
                  value={searchFilters.visionCategory}
                  onValueChange={(value) =>
                    onSearchFilters('visionCategory', value)
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('categorySelectPlaceholder')} />
                  </SelectTrigger>

                  <SelectContent>
                    {visionCategoriesAvailable.map((category) => (
                      <SelectItem key={category} value={category}>
                        {category}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="rounded-lg bg-white">
              <h3 className="mb-3 text-sm font-medium text-gray-700">
                {t('dateFromLabel')}
              </h3>
              <DateRangePicker
                dateFrom={searchFilters.dateFrom}
                onDateFromChange={(value) => onSearchFilters('dateFrom', value)}
              />
            </div>
          </div>

          <div className="flex flex-row flex-wrap rounded-lg bg-white p-4 shadow-sm">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
              <div>
                <h3 className="mb-3 text-sm font-medium text-gray-700">
                  {t('features')}
                </h3>

                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="pairsWith"
                      checked={!!searchFilters.pairsWith}
                      onCheckedChange={(checked: boolean) => {
                        onSearchFilters('pairsWith', checked || undefined);
                      }}
                    />

                    <Label htmlFor="pairsWith" className="text-sm">
                      {t('filters.pairsWith')}
                    </Label>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="mb-3 text-sm font-medium text-gray-700">
                  {t('status')}
                </h3>

                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="availableProduct"
                      checked={!!searchFilters.availableProduct}
                      onCheckedChange={(checked: boolean) => {
                        onSearchFilters(
                          'availableProduct',
                          checked || undefined,
                        );
                      }}
                    />
                    <Label htmlFor="availableProduct" className="text-sm">
                      {t('filters.availableProduct')}
                    </Label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showFilters && useSemanticSearch && (
        <div className="mt-6 space-y-4 rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h3 className="text-sm font-medium text-slate-700">
                {t('semantic.title')}
              </h3>
              <p className="text-xs text-slate-500">
                {t('semantic.description')}
              </p>
            </div>

            {isSemanticFiltersLoading && (
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <LoaderCircle className="h-4 w-4 animate-spin" />
                {t('semantic.loading')}
              </div>
            )}
          </div>

          <div>
            <h4 className="mb-3 text-sm font-medium text-slate-700">
              {t('status')}
            </h4>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="semanticAvailableProduct"
                checked={!!searchFilters.availableProduct}
                onCheckedChange={(checked: boolean) => {
                  onSearchFilters('availableProduct', checked || undefined);
                }}
              />
              <Label htmlFor="semanticAvailableProduct" className="text-sm">
                {t('filters.availableProduct')}
              </Label>
            </div>
          </div>

          <div>
            <h4 className="mb-3 text-sm font-medium text-slate-700">
              {t('semantic.category')}
            </h4>
            {semanticFilterFacets?.category?.length ? (
              <div className="grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-4">
                {semanticFilterFacets.category.map((option) => {
                  const checked = semanticFilters.categories.includes(
                    option.value,
                  );

                  return (
                    <label
                      key={option.value}
                      className="flex items-center justify-between gap-3 rounded-md border border-slate-200 px-3 py-2"
                    >
                      <div className="flex items-center gap-2">
                        <Checkbox
                          checked={checked}
                          onCheckedChange={(value: boolean) => {
                            handleToggleSemanticCategory(option.value, value);
                          }}
                        />
                        <span className="text-sm text-slate-700">
                          {option.value}
                        </span>
                      </div>
                      <Badge variant="secondary">{option.count}</Badge>
                    </label>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-slate-500">{t('semantic.empty')}</p>
            )}
          </div>

          <div>
            <h4 className="mb-3 text-sm font-medium text-slate-700">
              {t('semantic.attributes')}
            </h4>
            {featureEntries.length ? (
              <Accordion type="multiple" className="w-full">
                {featureEntries.map(([featureKey, options]) => (
                  <AccordionItem key={featureKey} value={featureKey}>
                    <AccordionTrigger className="py-3 no-underline hover:no-underline">
                      <div className="flex w-full items-center justify-between gap-3 pr-4">
                        <span>{formatFacetLabel(featureKey)}</span>
                        <Badge variant="outline">{options.length}</Badge>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-3">
                        {options.map((option) => {
                          const checked =
                            semanticFilters.features?.[featureKey]?.includes(
                              option.value,
                            ) ?? false;

                          return (
                            <label
                              key={`${featureKey}-${option.value}`}
                              className="flex items-center justify-between gap-3 rounded-md border border-slate-200 px-3 py-2"
                            >
                              <div className="flex items-center gap-2">
                                <Checkbox
                                  checked={checked}
                                  onCheckedChange={(value: boolean) => {
                                    handleToggleSemanticFeature(
                                      featureKey,
                                      option.value,
                                      value,
                                    );
                                  }}
                                />
                                <span className="text-sm text-slate-700">
                                  {option.value}
                                </span>
                              </div>
                              <Badge variant="secondary">{option.count}</Badge>
                            </label>
                          );
                        })}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            ) : (
              <p className="text-sm text-slate-500">{t('semantic.empty')}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
